import { useEffect, useRef, useLayoutEffect } from "react";
import { fetchGlobalData } from "../../utils/utils";
import { useQuery } from "@tanstack/react-query";
import HomepagePartners from "../../components/HomepagePartners/HomepagePartners";
import HomepagePartnersNew from "../../components/HomepagePartnersNew/HomepagePartnersNew";
import HomepageProducts from "../../components/HomepageProducts/HomepageProducts";
import HomepageInNumbers from "../../components/HomepageInNumbers/HomepageInNumbers";
import HomepageCountries from "../../components/HomepageCountries/HomepageCountries";
import { Homepage_Plain } from "../../types/api/homepage.ts";
import HomepageConnect from "../../components/HomepageConnect/HomepageConnect";
import FooterForm from "../../components/FooterForm/FooterForm";
import HomepageAbout from "../../components/HomepageAbout/HomepageAbout.tsx";
import HomepageSliderUkraine from "../../components/HomepageSliderUkraine/HomepageSliderUkraine.tsx";
import { updateSEO } from "../../utils/seo";

const HomeUkraine = ({ locale }: { locale: string }) => {
  const {
    data: homepage,
    error,
    isLoading,
    isError,
  } = useQuery<Homepage_Plain>({
    queryKey: ["/api/homepage-ukraine?customPopulate=nested&locale=" + locale],
    queryFn: ({ queryKey }) => fetchGlobalData(queryKey[0] as string),
  });

  const parallaxBlockRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  /*const sliderRef = useRef<HTMLDivElement>(null);*/

  // useLayoutEffect to calculate height after render, but before the browser paints
  useLayoutEffect(() => {
    if (!homepage || !parallaxBlockRef.current || !wrapRef.current) return;

    const parallaxBlock = parallaxBlockRef.current;
    const wrap = wrapRef.current;

    let hasSetHeight = false;
    //let parallaxNode = parallaxBlockRef.current;

    const checkHeight = () => {
      const wrapHeight = wrap?.offsetHeight || 0;
      if (wrapHeight > 0 && !hasSetHeight) {
        parallaxBlock.style.height = `${wrapHeight}px`;
        hasSetHeight = true;
      } else if (!hasSetHeight) {
        // If height is still 0, try again with a small delay
        setTimeout(checkHeight, 100);
      }
    };

    checkHeight();

    // Cleanup on unmount
    return () => {
      hasSetHeight = false;
      parallaxBlock.style.height = "";
    };
  }, [homepage]); // Dependency on homepage for height updates

  // useEffect for adding/removing the fixed class using scroll and touchmove
  useEffect(() => {
    if (!parallaxBlockRef.current || !homepage) return;

    let animationFrameId: number | null = null;

    const updateFixedClass = () => {
      const block = parallaxBlockRef.current;
      if (!block) return;

      const blockRect = block.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Add fixed class if the bottom of the window touches or is below the bottom of the block
      if (windowHeight >= blockRect.bottom) {
        block!.classList.add("fixed");
      } else {
        block!.classList.remove("fixed");
      }

      animationFrameId = requestAnimationFrame(updateFixedClass);
    };

    // Start animation on scroll
    const handleScroll = () => {
      if (!animationFrameId) {
        animationFrameId = requestAnimationFrame(updateFixedClass);
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Add touchmove listener for mobile devices (inertia)
    window.addEventListener("touchmove", handleScroll);

    // Cleanup listeners and animation on unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [homepage]); // Dependency on homepage for initialization after data loads

  // useEffect for parallax effect, activated when the fixed class is present
  useEffect(() => {
    if (!wrapRef.current || !parallaxBlockRef.current || !homepage) return;

    let animationFrameIdParallax: number | null = null;
    let initialScrollY = 0; // Store the initial scrollY value when fixed
    let currentMarginBottom = 0; // Store the current margin-bottom value

    const updateParallax = () => {
      const block = parallaxBlockRef.current;
      const wrap = wrapRef.current;
      if (!block || !wrap) return;

      // Check if the block has the fixed class
      if (block.classList.contains("fixed")) {
        const currentScrollY = window.scrollY;
        const parallaxSpeed = 0.3; // Parallax speed (less than 1 — slower than page scroll)

        // If this is the first frame after fixation, set the initial margin-bottom to 0
        if (initialScrollY === 0) {
          wrap.style.marginBottom = "0"; // Start at 0
          initialScrollY = currentScrollY; // Store the initial scrollY value
          currentMarginBottom = 0; // Reset the current margin-bottom value
        }

        // Calculate offset relative to initial scrollY for smooth movement in both directions
        const deltaScrollY = currentScrollY - initialScrollY;
        const newMarginBottom = deltaScrollY * parallaxSpeed; // Can be positive (down) or negative (up)

        // Update margin-bottom, but prevent negative values (mirrored behavior)
        currentMarginBottom = Math.max(0, newMarginBottom); // Limit to >= 0
        wrap.style.marginBottom = `${currentMarginBottom}px`; // Set only positive values
      } else {
        // Reset margin-bottom and initial value if the block is not fixed
        wrap.style.marginBottom = "0";
        initialScrollY = 0; // Reset the initial scrollY value
        currentMarginBottom = 0; // Reset the current margin-bottom value
      }

      animationFrameIdParallax = requestAnimationFrame(updateParallax);
    };

    // Start parallax on scroll, only if the block is fixed
    const handleScroll = () => {
      if (!animationFrameIdParallax) {
        animationFrameIdParallax = requestAnimationFrame(updateParallax);
      }
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Add touchmove listener for mobile devices (inertia)
    window.addEventListener("touchmove", handleScroll);

    // Cleanup listeners and animation on unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
      if (animationFrameIdParallax) {
        cancelAnimationFrame(animationFrameIdParallax);
      }
    };
  }, [homepage]); // Dependency on homepage for initialization after data loads

  // Update SEO and canonical when homepage data loads
  useEffect(() => {
    updateSEO(homepage?.data?.seo); // Canonical will be set even if seo is undefined
  }, [homepage]);

  if (isLoading)
    return <div className="loading-block" data-testid="loading-block"></div>;
  if (isError) return <div>Error: {error.message}</div>;
  if (!homepage) return <div>Global error: fetch homepage</div>;

  return (
    <>
      <div className="parallax-content-block" ref={parallaxBlockRef}>
        <div className="parallax-content-block__wrap" ref={wrapRef}>
          <HomepageSliderUkraine
            data={homepage.data.slider}
            products={homepage.data.productsCategory}
            globalVersion={homepage.data.globalVersion}
          />
          <HomepagePartners data={homepage.data.partners} />
        </div>
      </div>
      <div className="content-block content-block_ukraine">
        <HomepageAbout data={homepage.data.about} />
        <HomepageProducts
          data={homepage.data.productsCategory}
          title={homepage.data.productsTitle}
        />
        {/*homepage.data.link ? (
          <div className="link-download">
            <div className="wrapper">
              <a
                className="btn-black"
                href={homepage.data.link.link}
                target={homepage.data.link.target}
              >
                {homepage.data.link.title}
              </a>
            </div>
          </div>
        ) : (
          ""
        )*/}
        <HomepageInNumbers data={homepage.data.inNumbers} />
        <HomepageCountries data={homepage.data.countries} />
        <HomepagePartnersNew data={homepage.data.partnersNew} />
        <HomepageConnect data={homepage.data.connect} />
        <FooterForm
          data={homepage.data.contacts}
          office={homepage.data.connect.office}
          locale={locale}
        />
      </div>
    </>
  );
};

export default HomeUkraine;
